import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
  CircularProgress,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { get_notifications, get_notification_count, clear_notifications } from "../../api/ControllApi";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const ensureArray = (data) => {
  if (Array.isArray(data)) return data;
  if (!data) return [];
  if (typeof data === "object") {
    // Check for specific keys in the response
    const items = data.notifications || data.items || data;
    if (Array.isArray(items)) return items;
    // Handle objects with numeric keys
    const values = Object.values(items);
    if (values.length > 0 && (items[Object.keys(items)[0]]?.id || items[0]?.id)) {
      return values;
    }
  }
  return [];
};

const cleanMessage = (msg) => {
  if (!msg) return "";
  return msg
    .replace(/&#8377;/g, "₹")
    .replace(/\u00e2\u201a\u00b9/g, "₹")
    .replace(/&nbsp;/g, " ");
};

const NotificationDropdown = ({ count, onCountUpdate }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const open = Boolean(anchorEl);

  const handleOpen = async (event) => {
    setAnchorEl(event.currentTarget);
    fetchNotifications();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await get_notifications();
      if (res.status) {
        setNotifications(ensureArray(res.data));
      }
    } catch (err) {
      console.error("Failed to fetch notifications", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClearAll = async () => {
    try {
      const res = await clear_notifications();
      if (res.status) {
        setNotifications([]);
        if (onCountUpdate) onCountUpdate(0);
        handleClose();
      }
    } catch (err) {
      console.error("Failed to clear notifications", err);
    }
  };

  const handleNotificationClick = (notification) => {
    handleClose();
    // Logic for navigation based on type can be added here
    // For now, only close the menu or navigate to a general dashboard
    if (notification.type === "wallet") {
      navigate("/dashboard");
    }
    // Add more types as needed
  };

  return (
    <Box>
      <IconButton onClick={handleOpen} color="inherit" sx={{ p: 0 }}>
        <Badge badgeContent={count} color="error">
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              background: "linear-gradient(to right, #C7DD94, #92AF5D)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src="/assets/icons/notifications.png"
              alt="notification"
              style={{ width: "60%", height: "60%" }}
            />
          </Box>
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: { xs: "90vw", sm: 360 },
            maxHeight: 480,
            mt: 1.5,
            boxShadow: "0px 4px 20px rgba(0,0,0,0.1)",
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            "& .MuiList-root": { p: 0 },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        disableScrollLock
      >
        <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center", bgcolor: "#f8f9fa" }}>
          <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 700 }}>
            Notifications
          </Typography>
          {notifications.length > 0 && (
            <Button size="small" onClick={handleClearAll} sx={{ textTransform: "none" }}>
              Mark all read
            </Button>
          )}
        </Box>
        <Divider />

        <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <img src="/assets/images/nodatafound.png" alt="No data" style={{ width: 80, opacity: 0.5 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                No new notifications
              </Typography>
            </Box>
          ) : (
            <List>
              {notifications.slice(0, 10).map((notification) => (
                <React.Fragment key={notification.id}>
                  <ListItem
                    button
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      px: 2,
                      py: 1.5,
                      "&:hover": { bgcolor: "#f0f7ff" },
                      bgcolor: notification.is_read ? "transparent" : "#fff9e6",
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {notification.title}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" sx={{ fontSize: "0.85rem", color: "text.primary", mb: 0.5 }}>
                            {cleanMessage(notification.message)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {dayjs(notification.created_at).fromNow()}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>

        {notifications.length > 0 && (
          <Box sx={{ p: 1, textAlign: "center", borderTop: "1px solid #eee", bgcolor: "#fff", position: "sticky", bottom: 0 }}>
            <Button 
              fullWidth 
              size="small" 
              sx={{ textTransform: "none", fontWeight: 600 }} 
              onClick={() => {
                handleClose();
                navigate("/dashboard/notifications");
              }}
            >
              View all
            </Button>
          </Box>
        )}
      </Menu>
    </Box>
  );
};

export default NotificationDropdown;
